import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// المنسدلة الدستورية v4.1 - بوابة Portal: القائمة فوق كل شيء دائما
const arab = "'Tajawal','Cairo',system-ui,sans-serif";

export default function TmSelect({ value, onChange, options, placeholder = "اختر", size = "md", style = {} }) {
  const [open, setOpen] = useState(false);
  const [hov, setHov] = useState(-1);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const openMenu = () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom - 14;
    const spaceAbove = r.top - 14;
    const up = spaceBelow < 200 && spaceAbove > spaceBelow;
    const maxH = Math.max(140, Math.min(260, up ? spaceAbove : spaceBelow));
    setPos({ top: r.bottom + 6, bottomOff: window.innerHeight - r.top + 6, up, maxH, left: r.left, width: r.width });
    const _o = (options || []).map((o) => Array.isArray(o) ? o : [o, String(o)]);
    const _i = _o.findIndex((o) => o[0] === value);
    setHov(_i >= 0 ? _i : 0);
    setOpen(true);
  };
  const onKey = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") { e.preventDefault(); openMenu(); }
      return;
    }
    const _o = (options || []).map((o) => Array.isArray(o) ? o : [o, String(o)]);
    if (e.key === "ArrowDown") { e.preventDefault(); setHov((h) => Math.min(_o.length - 1, (h < 0 ? -1 : h) + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHov((h) => Math.max(0, (h < 0 ? _o.length : h) - 1)); }
    else if (e.key === "Home") { e.preventDefault(); setHov(0); }
    else if (e.key === "End") { e.preventDefault(); setHov(_o.length - 1); }
    else if (e.key === "Enter") { e.preventDefault(); setHov((h) => { if (h >= 0 && _o[h]) { onChange(_o[h][0]); setOpen(false); } return h; }); }
    else if (e.key === "Tab") { setOpen(false); }
  };

  useEffect(() => {
    if (!open) return;
    const away = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const shut = (e) => { if (popRef.current && e.target && popRef.current.contains && popRef.current.contains(e.target)) return; setOpen(false); };
    document.addEventListener("mousedown", away);
    window.addEventListener("scroll", shut, true);
    window.addEventListener("resize", shut);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("scroll", shut, true);
      window.removeEventListener("resize", shut);
    };
  }, [open]);

  useEffect(() => {
    if (!open || hov < 0 || !popRef.current) return;
    const el = popRef.current.children[hov + 1];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [hov, open]);

  const opts = (options || []).map((o) => Array.isArray(o) ? o : [o, String(o)]);
  const cur = opts.find((o) => o[0] === value);
  const pad = size === "sm" ? "5px 10px" : "10px 13px";
  const fs = size === "sm" ? 12.5 : 14;
  const ink = "color-mix(in srgb, var(--accent) 62%, #eef2ff)";
  const sh = "color-mix(in srgb, var(--accent) 38%, transparent)";
  const hl = "color-mix(in srgb, var(--accent) 16%, transparent)";

  return (
    <div style={{ position: "relative", ...style }}>
      <button ref={btnRef} type="button" onClick={openMenu} onKeyDown={onKey}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          fontFamily: arab, fontSize: fs, fontWeight: 700, textAlign: "right", cursor: "pointer",
          padding: pad, borderRadius: 0, color: "var(--text)",
          border: "1.5px solid " + (open ? "var(--accent)" : ink),
          background: "transparent",
          boxShadow: open ? "3px 3px 0 " + ink : "none",
          transform: open ? "translate(-1px,-1px)" : "none",
          transition: "all .25s cubic-bezier(.34,1.56,.64,1)" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cur ? cur[1] : placeholder}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .25s cubic-bezier(.34,1.56,.64,1)" }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="square" />
        </svg>
      </button>
      {open && pos && createPortal(
        <div ref={popRef} dir="rtl" style={{ position: "fixed", top: pos.up ? "auto" : pos.top, bottom: pos.up ? pos.bottomOff : "auto", left: pos.left, minWidth: pos.width, zIndex: 99999,
          background: "var(--surface)", border: "1.5px solid " + ink, borderRadius: 0,
          boxShadow: "4px 4px 0 " + ink, transformOrigin: pos.up ? "bottom" : "top", maxHeight: pos.maxH || 260, overflowY: "auto",
          animation: "tmsPop .28s cubic-bezier(.34,1.56,.64,1) both" }}>
          <style>{`
            @keyframes tmsPop { 0% { opacity: 0; transform: scaleY(.7); } 70% { transform: scaleY(1.02); } 100% { opacity: 1; transform: scaleY(1); } }
            @keyframes tmsUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
          `}</style>
          {opts.map(([v, l], i) => (
            <button key={String(v)} type="button"
              onClick={() => { onChange(v); setOpen(false); }}
              onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(-1)}
              style={{ display: "block", width: "100%", textAlign: "right", fontFamily: arab, fontSize: fs, fontWeight: v === value ? 800 : 600,
                padding: size === "sm" ? "7px 11px" : "10px 13px", border: "none", borderRadius: 0, cursor: "pointer",
                color: "var(--text)",
                background: hov === i ? hl : (v === value ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent"),
                animation: "tmsUp .22s cubic-bezier(.16,1,.3,1) both", animationDelay: (i * 26) + "ms",
                transition: "background .12s" }}>
              {l}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
